import { MutationTree, ActionTree, Module } from 'vuex';
import { RootState, ResultsState } from './../types';
import FRTBApi from '@/api/frtb.api';
import _ from 'lodash';

const state: ResultsState = {
  result: {},
  calculating: false,
  numCalculating: 0,
  downloading: false,
  selectedGraphIndex: 0,
  
  queuedCalculateRequest: {},
  calculateCancelActive: false,
  clearGraphStickyColumnsTrigger: 0
};

const mutations: MutationTree <ResultsState> = {
  setResult(resultsState, result: any = {}) {
    resultsState.result = result;
  },
  incCalculating(resultsState) {
    resultsState.numCalculating += 1;
    resultsState.calculating = true;
  },
  decCalculating(resultsState) {
    resultsState.numCalculating -= 1;
    if (resultsState.numCalculating <= 0) {
      resultsState.calculating = false;
    }
  },
  setDownloading(resultsState, downloading: boolean) {
    resultsState.downloading = downloading;
  },
  setQueuedCalculateRequest(resultsState, queuedCalculateRequest: any = {}) {
    resultsState.queuedCalculateRequest = queuedCalculateRequest;
  },
  setCalculateCancelActive(resultsState, cca: any = {}) {
    resultsState.calculateCancelActive = cca;
  },
  setSelectedGraphIndex(resultsState, selectedGraphIndex: any = {}) {
    resultsState.selectedGraphIndex = selectedGraphIndex;
  },

  triggerClearGraphStickyColumns(resultsState) {
    resultsState.clearGraphStickyColumnsTrigger = resultsState.clearGraphStickyColumnsTrigger + 1;
  }
};

const actions: ActionTree <ResultsState, RootState> = {
  /**
   * Calculate gets called from two places.
   * 
   * It either gets called in a more declarative manner from the 
   * populateFromProfile action (which is triggered when a profile is 
   * loaded/ selected).
   * 
   * OR, it gets called (kind of reactively), when changes to selected
   * attributes, data sources, or buckets are detected. The reactive listeners
   * for these changes are in CreateProfile.
   * 
   * @param context 
   * @param param1 
   */
  async calculateResults(context, {profile, force}) {
    context.commit('setQueuedCalculateRequest', profile);

    if (context.state.calculateCancelActive) {
      // it's in queue, and another cancel is ongoing, so return.
      return;
    } else if (context.state.calculating) {
      // cancel existing
      context.commit('setCalculateCancelActive', true);
      await FRTBApi.calculateResultsCancel();
      context.commit('setCalculateCancelActive', false);
    }

    // run queued req
    context.commit('incCalculating');

    profile = context.state.queuedCalculateRequest;
    context.commit('setQueuedCalculateRequest', null);


    try {
      context.commit('setCurrentProfileShowingOnGraph', profile);
      const obj = await FRTBApi.calculateResults(profile);
      if (obj) {
        context.commit('setResult', obj.res);
        return true;
      }
    } catch (err) {
      context.commit('setToastError', 'Error calculating results.');
    } finally {
      context.commit('decCalculating');
    }
  },
  async downloadResults(context) {
    context.commit('setDownloading', true);

    try {
      const res = await FRTBApi.downloadResults(context.rootState.profiles.profileShowingOnGraph);
      return res;
    } catch (e) {
      context.commit('setToastError', 'Error');
    }
    
    context.commit('setDownloading', false);
  }
};

export const results: Module<ResultsState, RootState> = {
  state,
  mutations,
  actions
};
